import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";

import type { FiscalProfileStepRef } from "@/pages/onboarding/FiscalProfileStep";
import type { WhoSetupChoice } from "@/pages/onboarding/WhoSetupStep";
import { getFiscalProfile } from "@/services/fiscalService";
import {
  getTenantCurrent,
  updateTenant,
  skipFiscalProfile,
  skipDgiCertificate,
  uploadCurrentTenantImage,
  getTenantPlans,
  updateTenantPlan,
  sendOnboardingSupportRequest,
  getCurrentPartnerBenefit,
  type TenantCurrentResponse,
  type TenantPlanOption,
  type PartnerBenefitStatus,
} from "@/services/authService";
import { createPlanCheckout } from "@/services/planPaymentsService";
import {
  planUsesActivationPath,
  resolvePlanSaveErrorMessage,
} from "@/pages/onboarding/planActivation";
import { useAuth } from "@/contexts/AuthContext";
import type { FiscalBenefitDeclarationValue } from "@/components/features/fiscal-benefit/FiscalBenefitDeclaration";
import {
  getCurrentTenantRucInfo,
  type CurrentTenantRucInfoResponse,
} from "@/services/authService";
import {
  getFiscaloSubstepStorageKey,
} from "@/pages/onboarding/CertificateStepContent";

/** Normalize DGI or form date to YYYY-MM-DD for API. */
function toActivityStartDateYYYYMMDD(value: string): string | null {
  const t = value.trim();
  if (!t) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
  const d = new Date(t);
  return Number.isNaN(d.getTime())
    ? null
    : d.toISOString().slice(0, 10);
}

export interface UseOnboardingTenantOptions {
  onSkipFiscalSuccess?: () => void;
}

export function useOnboardingTenant(options: UseOnboardingTenantOptions = {}) {
  const { onSkipFiscalSuccess } = options;
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();

  const [tenant, setTenant] = useState<TenantCurrentResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [legalName, setLegalName] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [department, setDepartment] = useState("");
  const [phone, setPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [literalE, setLiteralE] = useState<boolean | null>(null);
  const [literalEProofFile, setLiteralEProofFile] = useState<File | null>(null);
  const [industry, setIndustry] = useState("");
  const [profession, setProfession] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [certificatePath, setCertificatePath] = useState<
    "manual" | "fiscalo" | null
  >(null);
  const [fiscaloCertSubstep, setFiscaloCertSubstep] = useState<0 | 1>(0);
  const [supportRequestSent, setSupportRequestSent] = useState(false);
  const [supportRequestSentMigration, setSupportRequestSentMigration] =
    useState(false);
  const [requestingSupport, setRequestingSupport] = useState(false);
  const [isMigrating, setIsMigrating] = useState<boolean | null>(null);
  const [plans, setPlans] = useState<TenantPlanOption[]>([]);
  const [partnerBenefit, setPartnerBenefit] =
    useState<PartnerBenefitStatus | null>(null);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  /**
   * Decreto 206/019 declaration, answered on the plan step.
   *
   * Local until the plan is activated, and NEVER defaulted: an untouched form
   * stays `{ complies: null }` and writes nothing. "Declined" and "never asked"
   * are precisely the two states this record exists to tell apart, so the fix for
   * an unanswered form is to block Next, not to invent an answer.
   */
  const [benefitDeclaration, setBenefitDeclaration] =
    useState<FiscalBenefitDeclarationValue>({ complies: null, motivo: null });
  const [selectedBillingInterval, setSelectedBillingInterval] = useState<
    "monthly" | "annual"
  >("monthly");
  const [savingPlan, setSavingPlan] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [savingLiteralE, setSavingLiteralE] = useState(false);
  const [fiscalStepCanAdvance, setFiscalStepCanAdvance] = useState(false);
  const [savingFiscal, setSavingFiscal] = useState(false);
  const [skipFiscalLoading, setSkipFiscalLoading] = useState(false);
  const [skipDgiCertificateLoading, setSkipDgiCertificateLoading] =
    useState(false);
  const fiscalStepRef = useRef<FiscalProfileStepRef>(null);
  const [whoSetupChoice, setWhoSetupChoice] = useState<WhoSetupChoice | null>(
    null
  );
  const [savingWhoSetup, setSavingWhoSetup] = useState(false);
  const rucInfoFetchedForRutRef = useRef<string | null>(null);
  const [rucInfo, setRucInfo] = useState<CurrentTenantRucInfoResponse | null>(null);

  const isAccountantFlowOwner =
    tenant?.onboardingMode === "accountant" &&
    (user?.role === "owner" || user?.role === "admin");
  const { data: fiscalProfile, isFetched: isFiscalProfileFetched } = useQuery({
    queryKey: ["fiscal-profile"] as const,
    queryFn: getFiscalProfile,
    retry: false,
    enabled: Boolean(tenant?.id),
  });
  const hasFiscalProfile = Boolean(fiscalProfile);
  /** True when we don't need fiscal profile for step restoration, or the query has settled. */
  const isFiscalProfileSettled =
    isAccountantFlowOwner || !tenant?.id || isFiscalProfileFetched;

  const step1Complete = Boolean(tenant?.name?.trim() && tenant?.rut?.trim());
  const isUnipersonalOrMonotributo =
    tenant?.companyType === "unipersonal" || tenant?.companyType === "monotributo";
  const activityFieldValid = isUnipersonalOrMonotributo
    ? profession.trim() !== ""
    : industry.trim() !== "";
  const step1Valid =
    address.trim() !== "" &&
    city.trim() !== "" &&
    department.trim() !== "" &&
    phone.trim() !== "" &&
    activityFieldValid;

  const loadTenant = useCallback(async () => {
    if (!user?.tenantId) {
      setTenant(null);
      setLoading(false);
      return;
    }
    try {
      const data = await getTenantCurrent();
      setTenant(data);
      setLegalName((prev) => data.legalName?.trim() ?? prev ?? "");
      setAddress((prev) => data.address?.trim() ?? prev ?? "");
      setCity((prev) => data.city?.trim() ?? prev ?? "");
      setDepartment((prev) => data.department?.trim() ?? prev ?? "");
      setPhone((prev) => data.phone?.trim() ?? prev ?? "");
      setContactEmail((prev) => data.contactEmail?.trim() ?? prev ?? "");
      setLiteralE(data.literalE ?? null);
      setIsMigrating(data.migratingFromAnotherPlatform ?? null);
      setIndustry(data.industry ?? "");
      setProfession(data.profession ?? "");
      setSelectedPlanId(data.planId ?? null);
      setSelectedBillingInterval(
        (data.billingInterval as "monthly" | "annual") ?? "monthly"
      );
    } catch {
      setTenant(null);
    } finally {
      setLoading(false);
    }
  }, [user?.tenantId]);

  useEffect(() => {
    void loadTenant();
  }, [loadTenant]);

  useEffect(() => {
    if (user?.tenantId && !tenant) {
      setLoading(true);
      void loadTenant();
    }
  }, [user?.tenantId, tenant, loadTenant]);

  useEffect(() => {
    if (!tenant?.onboardingCompletedAt) {
      getTenantPlans().then(setPlans).catch(() => setPlans([]));
      getCurrentPartnerBenefit()
        .then(setPartnerBenefit)
        .catch(() => setPartnerBenefit(null));
    }
  }, [tenant?.onboardingCompletedAt]);

  useEffect(() => {
    const rut = tenant?.rut?.trim();
    if (!rut) return;
    if (rucInfoFetchedForRutRef.current === rut) return;
    setRucInfo(null);
    getCurrentTenantRucInfo()
      .then((res) => {
        if (!res.valid) return;
        rucInfoFetchedForRutRef.current = rut;
        setRucInfo(res);
        setLegalName((prev) =>
          prev.trim() ? prev : (res.legalName ?? "").trim()
        );
        setAddress((prev) =>
          prev.trim() ? prev : (res.legalAddress ?? "").trim()
        );
        setCity((prev) =>
          prev.trim() ? prev : (res.city ?? "").trim()
        );
        setDepartment((prev) =>
          prev.trim() ? prev : (res.department ?? "").trim()
        );
        setPhone((prev) =>
          prev.trim() ? prev : (res.phone ?? "").trim()
        );
        setContactEmail((prev) =>
          prev.trim() ? prev : (res.email ?? "").trim()
        );
      })
      .catch(() => {
        rucInfoFetchedForRutRef.current = null;
        setRucInfo(null);
      });
  }, [tenant?.rut]);

  // Hydrate the certificate path from the TENANT. This used to read
  // sessionStorage, which is why a value written by an older build could
  // outlive the code that wrote it: on 2026-09-29 a routing change could not
  // take effect for anyone with an open tab, and "open a new tab" was the only
  // remedy we could offer.
  //
  // The fiscalo SUB-step stays in sessionStorage on purpose — it is position
  // within a screen, not a routing input, and re-entering that screen at its
  // first sub-step is a fine outcome on a new device.
  useEffect(() => {
    const path = tenant?.onboardingCertPath ?? null;
    // Mirror null too. Returning early on anything but manual/fiscalo left the
    // local path stuck at its old value when the tenant's was cleared — by
    // cancelling out of the Fiscalo flow, or by an operator clearing it.
    setCertificatePath(path === "manual" || path === "fiscalo" ? path : null);
    if (path !== "fiscalo" || !tenant?.id) return;
    try {
      const substep = sessionStorage.getItem(
        getFiscaloSubstepStorageKey(tenant.id)
      );
      if (substep === "0" || substep === "1")
        setFiscaloCertSubstep(Number(substep) as 0 | 1);
    } catch {
      // ignore
    }
  }, [tenant?.id, tenant?.onboardingCertPath]);

  const saveProfile = useCallback(async (): Promise<boolean> => {
    setSavingProfile(true);
    try {
      await updateTenant({
        legalName: (legalName ?? "").trim().toUpperCase() || null,
        address: address || null,
        city: city || null,
        department: department || null,
        phone: phone || null,
        contactEmail: (contactEmail ?? "").trim() || null,
        activityStartDate:
          (rucInfo?.fechaInicio &&
            toActivityStartDateYYYYMMDD(rucInfo.fechaInicio)) ??
          undefined,
        language: "es",
        industry: industry || null,
        profession: profession || null,
      });
      await loadTenant();
      toast.success(t("onboarding.saved"));
      return true;
    } catch {
      toast.error(t("tenants.edit.errorUpdate"));
      return false;
    } finally {
      setSavingProfile(false);
    }
  }, [
    legalName,
    address,
    city,
    department,
    phone,
    contactEmail,
    industry,
    profession,
    rucInfo?.fechaInicio,
    loadTenant,
    t,
  ]);

  const handleLogoFileChange = useCallback(
    async (file: File | null): Promise<void> => {
      if (!file) return;
      setUploadingLogo(true);
      try {
        await uploadCurrentTenantImage(file, "both");
        await loadTenant();
        toast.success(t("onboarding.saved"));
      } catch {
        toast.error(t("tenants.edit.errorUpdate"));
      } finally {
        setUploadingLogo(false);
      }
    },
    [loadTenant, t]
  );

  const handleSavePlan = useCallback(
    async (planId: string, billingInterval: "monthly" | "annual") => {
      setSavingPlan(true);
      try {
        if (planUsesActivationPath(planId, billingInterval, plans)) {
          // Free OR partner-discounted paid: activate via updateTenantPlan,
          // which returns a dLocal redirectUrl for any partner-covered remainder.
          const res = await updateTenantPlan(planId, billingInterval, "onboarding");
          setSelectedPlanId(planId);
          setSelectedBillingInterval(billingInterval);
          await loadTenant();
          if (res.redirectUrl) {
            window.location.href = res.redirectUrl;
            return;
          }
          toast.success(t("onboarding.saved"));
          return;
        }
        const res = await createPlanCheckout({
          planId,
          billingInterval,
          returnContext: "onboarding",
        });
        await loadTenant();
        if (res.redirectUrl) {
          window.location.href = res.redirectUrl;
          return;
        }
        setSelectedPlanId(planId);
        setSelectedBillingInterval(billingInterval);
        toast.success(t("onboarding.saved"));
      } catch (error) {
        // Surface what the server actually said. The bare catch that used to
        // live here turned "Paid plans require completing checkout" — a real,
        // actionable routing bug — into an unattributable "try again" that
        // showed up in no log, because Nest does not log 400s.
        toast.error(resolvePlanSaveErrorMessage(error, t));
      } finally {
        setSavingPlan(false);
      }
    },
    // `plans` is read via planUsesActivationPath: leaving it out pinned this
    // callback to the empty catalog from first render, so every lookup missed
    // and every plan was treated as unknown.
    [loadTenant, plans, t]
  );

  const handleCertificateUploadSuccess = useCallback(async () => {
    await refreshUser();
    await loadTenant();
    toast.success(t("tenants.edit.dgi.configured"));
  }, [refreshUser, loadTenant, t]);

  const handleSkipFiscalProfile = useCallback(async () => {
    setSkipFiscalLoading(true);
    try {
      await skipFiscalProfile();
      await loadTenant();
      onSkipFiscalSuccess?.();
    } catch {
      toast.error(t("onboarding.errorSkip"));
    } finally {
      setSkipFiscalLoading(false);
    }
  }, [loadTenant, onSkipFiscalSuccess, t]);

  const handleSkipDgiCertificate = useCallback(async () => {
    setSkipDgiCertificateLoading(true);
    try {
      await skipDgiCertificate();
      await loadTenant();
    } catch {
      toast.error(t("onboarding.errorSkip"));
    } finally {
      setSkipDgiCertificateLoading(false);
    }
  }, [loadTenant, t]);

  const runSupport = useCallback(
    async (
      fn: () => Promise<void>,
      setSent: React.Dispatch<React.SetStateAction<boolean>>,
      successKey: string
    ) => {
      setRequestingSupport(true);
      try {
        await fn();
        setSent(true);
        toast.success(t(successKey));
      } catch {
        toast.error(t("tenants.edit.errorUpdate"));
      } finally {
        setRequestingSupport(false);
      }
    },
    [t]
  );
  const handleRequestCertificateSupport = useCallback(
    () =>
      runSupport(
        () => sendOnboardingSupportRequest("certificate_request"),
        setSupportRequestSent,
        "onboarding.supportRequestSent"
      ),
    [runSupport]
  );

  const handleRequestMigrationSupport = useCallback(
    () =>
      runSupport(
        () => sendOnboardingSupportRequest("migration_help"),
        setSupportRequestSentMigration,
        "onboarding.supportRequestSentMigration"
      ),
    [runSupport]
  );

  return {
    tenant,
    loading,
    rucInfo,
    legalName,
    setLegalName,
    address,
    setAddress,
    city,
    setCity,
    department,
    setDepartment,
    phone,
    setPhone,
    contactEmail,
    setContactEmail,
    industry,
    setIndustry,
    profession,
    setProfession,
    literalE,
    setLiteralE,
    literalEProofFile,
    setLiteralEProofFile,
    whoSetupChoice,
    setWhoSetupChoice,
    savingWhoSetup,
    setSavingWhoSetup,
    fiscalStepRef,
    fiscalStepCanAdvance,
    setFiscalStepCanAdvance,
    savingProfile,
    savingFiscal,
    setSavingFiscal,
    savingLiteralE,
    setSavingLiteralE,
    skipFiscalLoading,
    skipDgiCertificateLoading,
    certificatePath,
    setCertificatePath,
    fiscaloCertSubstep,
    setFiscaloCertSubstep,
    supportRequestSent,
    supportRequestSentMigration,
    requestingSupport,
    isMigrating,
    setIsMigrating,
    plans,
    partnerBenefit,
    selectedPlanId,
    benefitDeclaration,
    setBenefitDeclaration,
    setSelectedPlanId,
    selectedBillingInterval,
    setSelectedBillingInterval,
    savingPlan,
    setSavingPlan,
    uploadingLogo,
    hasFiscalProfile,
    isFiscalProfileSettled,
    step1Complete,
    step1Valid,
    loadTenant,
    saveProfile,
    handleLogoFileChange,
    handleSavePlan,
    handleCertificateUploadSuccess,
    handleSkipFiscalProfile,
    handleSkipDgiCertificate,
    handleRequestCertificateSupport,
    handleRequestMigrationSupport,
  };
}
